import _ from 'lodash';
import api from './ApiService';
import socket from './SocketService';
import store, { User } from '../store';

const TOKEN_NAME = 'token';

// init session token if persistent token exists
if (localStorage.getItem(TOKEN_NAME) && !sessionStorage.getItem(TOKEN_NAME)) {
  sessionStorage.setItem(TOKEN_NAME, localStorage.getItem(TOKEN_NAME) || '');
}

class Auth {
  getUser(): User {
    return store.getState().app.user;
  }

  getBranch() {
    return store.getState().app.branch;
  }

  hasPermission(pageName: string, branchId?: number, user?: User) {
    if (!user) {
      user = this.getUser();
    }
    if (!branchId) {
      branchId = store.getState().app.branch.id;
    }

    if (!branchId) {
      console.error('BranchId is not set');
    }

    if (!user || _.isEmpty(user)) {
      return false;
    }

    const permissions = this.getPermissions(branchId, user.permissions);

    return user.username === 'dsisupport' || permissions[pageName];
  }

  hasPermissionToMany(permissions: string[] = []) {
    if (permissions && permissions.length) {
      for (const perm of permissions) {
        if (!this.hasPermission(perm)) {
          return false;
        }
      }
    }

    return true;
  }

  hasPermissionToOne(permissions = []) {
    if (permissions && permissions.length) {
      for (const perm of permissions) {
        if (this.hasPermission(perm)) {
          return true;
        }
      }
    }

    return false;
  }

  hasCardTerminalPayments() {
    const branch = this.getBranch();
    return !!(branch.payment && branch.payment.enabled && branch.payment.resellerName);
  }

  getPermissions(branchId?: number, permissions?: string[]) {
    if (permissions === undefined) {
      permissions = this.getUser().permissions;
    }

    if (!permissions) {
      return {};
    }

    if (branchId) {
      return permissions[branchId] || {};
    }

    return permissions;
  }

  removeToken() {
    localStorage.removeItem(TOKEN_NAME);
    sessionStorage.removeItem(TOKEN_NAME);
  }

  getToken() {
    return localStorage.token;
  }

  getSessionToken() {
    return sessionStorage.token;
  }

  setToken(token: string) {
    localStorage.setItem(TOKEN_NAME, token);
    sessionStorage.setItem(TOKEN_NAME, token);
  }

  switchBranch(branchId: number) {
    return api
      .put(`branch/${branchId}/switch`)
      .then(({ data }) => {
        this.setToken(data.token);
        return Promise.resolve({ user: data.user });
      })
      .catch(() => {
        return Promise.reject(new Error('Failed to switch branch'));
      });
  }

  login({
    username,
    password,
    userId,
    mfaToken,
  }: {
    username: string;
    password: string;
    userId?: number;
    mfaToken?: string;
  }) {
    const payload = {
      username,
      password,
      userId,
      mfaToken,
    };

    return api
      .post('/auth/login', payload)
      .then(({ data }) => {
        const { user, token, mfaEnabled } = data;

        if (mfaEnabled) {
          return Promise.resolve(data);
        }

        localStorage.setItem('user', JSON.stringify(this.filterUserDetails(user)));
        this.setToken(token);
        socket.login();
        return Promise.resolve(data);
      })
      .catch(({ response }) => {
        return Promise.reject(new Error(response.data.error));
      });
  }

  logout() {
    api.post('/auth/logout');
    this.removeToken();
    socket.logout();
  }

  loggedIn() {
    if ((process.env.NODE_ENV as any) === 'testing') {
      return true;
    }

    return !!this.getToken();
  }

  mfaRequired() {
    return store.getState().app.mfaRequired;
  }

  resetPassword(email: string, captcha = '') {
    const payload = {
      email,
      captcha,
    };

    return api.post('auth/resetpassword', payload).catch(({ response }) => {
      return Promise.reject(new Error(response.data.error || `Error resetting password ${email}`));
    });
  }

  changePassword(password: string, token: string) {
    const payload = {
      password,
      token,
    };

    return api
      .post('auth/changepassword', payload)
      .then(({ data }) => {
        return Promise.resolve({ user: data.user, branch: data.branch });
      })
      .catch(({ response }) => {
        return Promise.reject(new Error(response.data.error || 'Error changing password'));
      });
  }

  isDsiSupport() {
    const user = this.getUser();
    return user && user.username === 'dsisupport';
  }

  filterUserDetails(details = {}) {
    return _.pick(details, ['id', 'username', 'email', 'ppc']);
  }
}

export default new Auth();
